import React from 'react';
import {
  Badge,
  Button,
  Card,
  Flex,
  Menu,
  MenuItem,
  MenuTrigger,
  Text,
  TextArea,
} from '@chatsdet/ui';
import {
  ArrowSquareOut,
  Check,
  DotsThreeVertical,
  DownloadSimple,
  NotePencil,
  Trash,
  X,
} from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import type { ContextDocument } from '../useContextLibrary';
import {
  fileTypeIcon,
  fileTypeLabel,
  formatBytes,
  NON_DOWNLOADABLE_TYPES,
  statusBadgeVariant,
} from '../fileTypeMeta';

type DocumentCardProps = {
  doc: ContextDocument;
  onDownload: (id: string) => void;
  onSaveDescription: (id: string, description: string | null) => void;
  onDelete: (id: string) => void;
  isSavingDescription: boolean;
};

/**
 * One library document rendered as a card: type icon + badge, name, an
 * inline-editable description (click to edit, saves on blur/Enter), a status
 * chip, age, and an actions menu (download/open · edit · delete). The card is
 * the unit of the library grid.
 */
export const DocumentCard: React.FC<DocumentCardProps> = ({
  doc,
  onDownload,
  onSaveDescription,
  onDelete,
  isSavingDescription,
}) => {
  const Icon = fileTypeIcon(doc.fileType);
  const isLink = doc.fileType === 'link';
  const canDownload = !NON_DOWNLOADABLE_TYPES.has(doc.fileType);

  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(doc.description ?? '');
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  // Keep the local draft in sync if the doc's description changes underneath us
  // (e.g. another tab edited it) while we're not actively editing.
  React.useEffect(() => {
    if (!isEditing) setDraft(doc.description ?? '');
  }, [doc.description, isEditing]);

  const commit = React.useCallback(() => {
    const next = draft.trim();
    const current = doc.description ?? '';
    if (next !== current) onSaveDescription(doc.id, next.length > 0 ? next : null);
    setIsEditing(false);
  }, [doc.description, doc.id, draft, onSaveDescription]);

  const sizeLabel = formatBytes(doc.fileSizeBytes);

  return (
    <Card padding="md" className="flex h-full flex-col gap-3">
      <Flex align="center" justify="between" gap="sm">
        <Flex align="center" gap="sm" className="min-w-0">
          <Icon size={18} weight="fill" className="shrink-0 text-muted-foreground" />
          <Badge variant="secondary" size="sm">
            {fileTypeLabel(doc.fileType)}
          </Badge>
        </Flex>

        <MenuTrigger>
          <Button
            variant="icon"
            size="sm"
            iconOnly
            tooltip="Document actions"
            className="h-6 shrink-0 text-muted-foreground/50 hover:text-muted-foreground"
          >
            <DotsThreeVertical size={16} />
          </Button>
          <Menu>
            {canDownload && (
              <MenuItem id="download" onAction={() => onDownload(doc.id)}>
                <Flex align="center" gap="sm">
                  {isLink ? <ArrowSquareOut size={14} /> : <DownloadSimple size={14} />}
                  {isLink ? 'Open link' : 'Download'}
                </Flex>
              </MenuItem>
            )}
            <MenuItem id="edit" onAction={() => setIsEditing(true)}>
              <Flex align="center" gap="sm">
                <NotePencil size={14} />
                Edit description
              </Flex>
            </MenuItem>
            <MenuItem id="delete" destructive onAction={() => setConfirmDelete(true)}>
              <Flex align="center" gap="sm">
                <Trash size={14} />
                Delete
              </Flex>
            </MenuItem>
          </Menu>
        </MenuTrigger>
      </Flex>

      <div className="min-w-0">
        <Text size="sm" weight="medium" className="block truncate" title={doc.fileName}>
          {doc.fileName}
        </Text>
      </div>

      {/* Description — inline editable. */}
      {isEditing ? (
        <div className="space-y-1.5">
          <TextArea
            aria-label="Document description"
            placeholder="Describe what this is and how an agent should use it…"
            value={draft}
            onChange={setDraft}
            rows={3}
            autoFocus
          />
          <Flex justify="end" gap="sm">
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              tooltip="Cancel"
              onPress={() => {
                setDraft(doc.description ?? '');
                setIsEditing(false);
              }}
            >
              <X size={14} />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              iconOnly
              tooltip="Save"
              onPress={commit}
              isPending={isSavingDescription}
            >
              <Check size={14} />
            </Button>
          </Flex>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className="min-h-[2.5rem] flex-1 rounded-md text-left text-xs leading-relaxed text-muted-foreground hover:text-foreground"
        >
          {doc.description?.trim() ? (
            doc.description
          ) : (
            <span className="italic text-muted-foreground/60">Add a description…</span>
          )}
        </button>
      )}

      <Flex align="center" justify="between" gap="sm" className="mt-auto pt-1">
        <Badge variant={statusBadgeVariant(doc.status)} size="sm">
          {doc.status}
        </Badge>
        <Text size="xs" className="text-muted-foreground">
          {sizeLabel ? `${sizeLabel} · ` : ''}
          {formatDistanceToNow(new Date(doc.createdAt), { addSuffix: true })}
        </Text>
      </Flex>

      {confirmDelete && (
        <div className="flex items-center justify-between rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1.5">
          <Text size="xs" className="text-destructive">
            Delete permanently?
          </Text>
          <Flex align="center" gap="sm">
            <Button
              variant="ghost"
              size="sm"
              onPress={() => setConfirmDelete(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onPress={() => {
                onDelete(doc.id);
                setConfirmDelete(false);
              }}
            >
              Delete
            </Button>
          </Flex>
        </div>
      )}
    </Card>
  );
};
